import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import { fetchMtrUnitList } from '#/api/imsMgmt/settings/mtr/units';
import { cn } from '#/lib/utils';
import { getRequestErrorMessage } from '#/utils/requestError';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialog3xlMaxWidthClassName,
  dialogBodyFillClassName,
  dialogContentCompactGapClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { SelectCore } from '#/modules/commonComponents/form/select';
import { buildLabelPrintRequest } from '#/modules/imsMgmt/inventoryStock/helper/buildPrintConfirmItems';

const PRINT_CONFIRM_ROW_CLASS = cn(
  'grid cursor-pointer items-center gap-3 px-3 py-3 transition-colors',
  'grid-cols-[auto_minmax(0,1fr)_minmax(0,1.35fr)_8.5rem]',
);

function buildInitialSelection(items) {
  return Object.fromEntries((items ?? []).map((item) => [item.id, true]));
}

function activeUnitOptions(units) {
  return (Array.isArray(units) ? units : [])
    .filter((unit) => unit?.status === 'ACTIVE' && unit?.id)
    .map((unit) => ({
      value: String(unit.id),
      label: unit.name || unit.code || String(unit.id),
    }));
}

const PrintConfirmUnitField = ({
  stockId,
  mtrId,
  baseUnit,
  value,
  onChange,
  disabled,
}) => {
  const unitsQuery = useQuery({
    queryKey: ['erp', 'mtr', 'units', mtrId],
    queryFn: () => fetchMtrUnitList(mtrId),
    enabled: Boolean(mtrId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const options = useMemo(
    () => activeUnitOptions(unitsQuery.data?.data),
    [unitsQuery.data],
  );
  const baseId = String(baseUnit?.id ?? '');
  const selected = value || baseId;
  const selectedInOptions = options.some((option) => option.value === selected);

  if (!mtrId) {
    return (
      <p className="text-xs text-muted-foreground">缺少物料，將印基本單位</p>
    );
  }

  if (unitsQuery.isError) {
    const reason = getRequestErrorMessage(unitsQuery.error, '無法載入單位');
    const baseName = baseUnit?.name || '基本單位';
    return (
      <p className="text-xs wrap-break-word text-destructive">
        {reason}
        {`（將以${baseName}列印）`}
      </p>
    );
  }

  return (
    <div
      className="min-w-0"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <SelectCore
        id={`print-unit-${stockId}`}
        options={options}
        value={selectedInOptions ? selected : ''}
        onChange={onChange}
        isDisabled={disabled || unitsQuery.isLoading}
        placeholder={unitsQuery.isLoading ? '載入單位…' : (baseUnit?.name || '基本單位')}
        noOptionsMessage="沒有有效單位"
        aria-label={`列印單位 ${stockId}`}
      />
    </div>
  );
};

PrintConfirmUnitField.propTypes = {
  stockId: PropTypes.string.isRequired,
  mtrId: PropTypes.string,
  baseUnit: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/**
 * 庫存列表：列印選取前確認（可單筆取消勾選；不同步外層列表勾選）。
 */
const InventoryStockPrintConfirmDialog = ({
  open,
  onOpenChange,
  items = [],
  isPrinting = false,
  onConfirm,
}) => {
  const [selectedById, setSelectedById] = useState(() => buildInitialSelection(items));
  const [unitByStockId, setUnitByStockId] = useState({});

  const selectedCount = useMemo(
    () => Object.values(selectedById).filter(Boolean).length,
    [selectedById],
  );

  function handleToggle(id, nextChecked) {
    setSelectedById((prev) => ({
      ...prev,
      [id]: nextChecked,
    }));
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleUnitChange(id, nextUnitId) {
    setUnitByStockId((prev) => ({
      ...prev,
      [id]: nextUnitId,
    }));
  }

  function handleConfirm() {
    const ids = items
      .map((item) => item.id)
      .filter((id) => selectedById[id]);
    if (ids.length === 0 || isPrinting) {
      return;
    }
    onConfirm?.(buildLabelPrintRequest(items, ids, unitByStockId));
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="確認列印標籤"
      description="可取消不需列印的庫存，並為每筆選擇列印單位。未改選時印基本單位。"
      headerVariant="muted"
      maxWidthClassName={dialog3xlMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      bodyClassName={dialogBodyFillClassName}
      closeOnOutsideClick={false}
      confirmDisabled={selectedCount === 0 || isPrinting}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText={isPrinting ? '列印中…' : `確認列印（${selectedCount}）`}
      cancelText="取消"
    >
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">沒有可列印的庫存。</p>
      ) : (
        <ul
          className={cn(
            'm-0 p-0 min-h-0 flex-1 list-none overflow-y-auto',
            'divide-y divide-slate-200 rounded-md border border-slate-200',
          )}
        >
          {items.map((item) => {
            const checked = Boolean(selectedById[item.id]);

            return (
              <li
                key={item.id}
                role="option"
                aria-selected={checked}
                className={cn(
                  PRINT_CONFIRM_ROW_CLASS,
                  checked
                    ? 'bg-primary-100 shadow-inner hover:bg-primary-100'
                    : 'hover:bg-slate-50',
                )}
                onClick={() => handleToggle(item.id, !checked)}
              >
                <div
                  className="flex items-center"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => handleToggle(item.id, value === true)}
                    aria-label={`列印 ${item.stockNo}`}
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-mono text-sm wrap-break-word text-slate-800">
                    {item.stockNo}
                  </p>
                  <p className="text-xs wrap-break-word text-muted-foreground">
                    {item.warehouseLabel || '—'}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium wrap-break-word text-gray-900">
                    {item.name || item.mtrNo || '—'}
                  </p>
                  <p className="font-mono text-xs wrap-break-word text-gray-500">
                    {item.mtrNo || '—'}
                  </p>
                </div>
                <PrintConfirmUnitField
                  stockId={item.id}
                  mtrId={item.mtrId}
                  baseUnit={item.baseUnit}
                  value={unitByStockId[item.id]}
                  onChange={(nextUnitId) => handleUnitChange(item.id, nextUnitId)}
                  disabled={isPrinting}
                />
              </li>
            );
          })}
        </ul>
      )}
    </AppDialog>
  );
};

InventoryStockPrintConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    stockNo: PropTypes.string,
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    name: PropTypes.string,
    warehouseLabel: PropTypes.string,
    baseUnit: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  })),
  isPrinting: PropTypes.bool,
  onConfirm: PropTypes.func,
};

export default InventoryStockPrintConfirmDialog;
